import React, { useState, useEffect } from "react";
import { useMoney } from "../../hooks/useMoney";
import { useAppDispatch, useAppSelector } from "../../store/hook";
import { getDistributorById, fetchDistributorProgramPaymentGateway } from "../../features/distributor/DistributorSlice";
import { fetchPaymentGateways, fetchPaymentGatewayKeysSchema, updatePaymentGatewayConfig, clearSelectedGatewaySchema, clearMessages } from "../../features/payment/PaymentGatewaySlice";
import { RootState } from "../../store/store";
import { ArrowLeft } from "lucide-react";
import { Loader2, AlertCircle, Plus, Trash2 } from "lucide-react";
import EditBuildCataloguePage from "./EditBuildCataloguePage";

interface EditDistributorPageProps {
  distributorId: number;
  onBack: () => void;
  onOpenAlertSettings?: () => void;
}



interface FormData {
  isPurchaseOrderRequired: boolean;
  allowPurchaseOrderCreation: boolean;
  currency: string;
  country: string;
  paymentType: number;
  name: string;
  businessName: string;
  whiteListedIps: string[];
  isNonUniqueReferenceIdAllowed: boolean;
  paymentGatewayId: number;
  paymentGatewayConfig: Record<string, any>;
  logo?: File | string | null;
  contactInformation: {
    email: string;
    phone: string;
    contactPerson: string;
    address: {
      city: string;
      state: string;
      street: string;
      country: string;
      zipCode: string;
    };
  };
  wlPortalDetails: {
    paymentGateway: string | null;
    color: string;
  };
  hostname: string;
}

const EditDistributorPage: React.FC<EditDistributorPageProps> = ({
  onBack,
  distributorId,
  onOpenAlertSettings,
}) => {
  const { code } = useMoney();
  const dispatch = useAppDispatch();
  const { loading, error, selectedDistributor } = useAppSelector((state: RootState) => state.distributor);
  const { paymentGateways, selectedGatewaySchema, selectedGatewayData, success } = useAppSelector((state: RootState) => state.paymentGateway);

  const [showBuildCatalogue, setShowBuildCatalogue] = useState(false);
  const [showPaymentGatewayModal, setShowPaymentGatewayModal] = useState(false);
  const [selectedGateway, setSelectedGateway] = useState<any>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [formData, setFormData] = useState<FormData>({
    currency: "",
    country: "",
    paymentType: 0,
    name: "",
    businessName: "",
    whiteListedIps: [],
    isNonUniqueReferenceIdAllowed: true,
    paymentGatewayId: 0,
    paymentGatewayConfig: {},
    logo: null,
    contactInformation: {
      email: "",
      phone: "",
      contactPerson: "",
      address: {
        city: "",
        state: "",
        street: "",
        country: "",
        zipCode: "",
      },
    },
    wlPortalDetails: {
      paymentGateway: null,
      color: "#FF0000"
    },
    hostname: "",
    isPurchaseOrderRequired: false,
    allowPurchaseOrderCreation: false
  });

  const [formErrors, setFormErrors] = useState({
    currency: "",
    country: "",
    paymentType: "",
    name: "",
    businessName: "",
    password: "",
    hostname: "",
    contactInformation: {
      email: "",
      phone: "",
      contactPerson: "",
      address: {
        street: "",
        city: "",
        state: "",
        country: "",
        zipCode: "",
      },
    },
  });

  const [newIp, setNewIp] = useState("");

  useEffect(() => {
    dispatch(getDistributorById(distributorId));
    dispatch(fetchPaymentGateways(distributorId));
    dispatch(fetchDistributorProgramPaymentGateway(distributorId));
    
    // Cleanup function to clear selected gateway schema on unmount
    return () => {
      dispatch(clearSelectedGatewaySchema());
    };
  }, []);

  useEffect(() => {
    if (selectedDistributor) {
      // Find the payment gateway name based on paymentGatewayId
      const selectedGateway = paymentGateways?.find(
        gateway => gateway.paymentGatewayId === Number(selectedDistributor.paymentGatewayId)
      );

      // Get color from theme or use default green
      const defaultColor = "#22C55E"; // Default green color
      const themeColor = (selectedDistributor as any)?.theme?.primary_color || defaultColor;

      setFormData(prevFormData => ({
        currency: selectedDistributor.currency || "",
        country: selectedDistributor.country || "",
        paymentType: selectedDistributor.paymentType,
        name: selectedDistributor.name || "",
        businessName: selectedDistributor.businessName || "",
        whiteListedIps: selectedDistributor.whiteListedIps || [],
        isNonUniqueReferenceIdAllowed: selectedDistributor.isNonUniqueReferenceIdAllowed ?? true,
        // Only update paymentGatewayId if it's currently 0 (not manually set by user)
        paymentGatewayId:
          prevFormData.paymentGatewayId === 0
            ? Number(selectedDistributor.paymentGatewayId) || 0
            : prevFormData.paymentGatewayId,
        paymentGatewayConfig: selectedDistributor.paymentGatewayConfig || {},
        logo: selectedDistributor.logo_url,
        contactInformation: {
          email: selectedDistributor.contactInformation?.email || "",
          phone: selectedDistributor.contactInformation?.phone || "",
          contactPerson: selectedDistributor.contactInformation?.contactPerson || "",
          address: {
            city: selectedDistributor.contactInformation?.address?.city || "",
            state: selectedDistributor.contactInformation?.address?.state || "",
            street: selectedDistributor.contactInformation?.address?.street || "",
            country: selectedDistributor.contactInformation?.address?.country || "",
            zipCode: selectedDistributor.contactInformation?.address?.zipCode || "",
          },
        },
        wlPortalDetails: {
          paymentGateway: selectedDistributor.paymentGatewayId?.toString() || null,
          color: themeColor
        },
        hostname: selectedDistributor.hostname || "",
        isPurchaseOrderRequired: selectedDistributor.isPurchaseOrderRequired ?? false,
        allowPurchaseOrderCreation: selectedDistributor.allowPurchaseOrderCreation ?? false
      }));

      // Set logo preview if logo_url exists
      if (selectedDistributor.logo_url) {
        setLogoPreview(selectedDistributor.logo_url);
      }
    }
  }, [selectedDistributor, paymentGateways]);

  useEffect(() => {
    // Clear selectedGatewaySchema when payment gateway is reset to 0
    if (formData.paymentGatewayId === 0) {
      dispatch(clearSelectedGatewaySchema());
    }
  }, [formData.paymentGatewayId, dispatch]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const keys = name.split(".");

    if (type === "checkbox") {
      setFormData({
        ...formData,
        [name]: (e.target as HTMLInputElement).checked,
      });
    } else if (keys.length === 2 && keys[1] === "phone") {
      const numericValue = value.replace(/\D/g, '');
      setFormData({
        ...formData,
        [keys[0]]: {
          ...(formData[keys[0] as keyof typeof formData] as object),
          [keys[1]]: numericValue,
        },
      });
    } else if (keys.length === 3) {
      setFormData({
        ...formData,
        [keys[0]]: {
          ...(formData[keys[0] as keyof typeof formData] as object),
          [keys[1]]: {
            ...((formData as any)[keys[0]][keys[1]] as object),
            [keys[2]]: value,
          },
        },
      });
    } else if (keys.length === 2) {
      setFormData({
        ...formData,
        [keys[0]]: {
          ...(formData[keys[0] as keyof typeof formData] as object),
          [keys[1]]: value,
        },
      });
    } else {
      setFormData({
        ...formData,
        [name]: name === "paymentType" ? parseInt(value, 10) : value,
      });
    }

    setFormErrors({ ...formErrors, [name]: "" });
  };

  const handleWhiteListedIpsChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const ips = e.target.value.split('\n').filter(ip => ip.trim() !== '');
    setFormData({
      ...formData,
      whiteListedIps: ips,
    });
  };

  const handlePaymentGatewayChange = async (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const gatewayId = parseInt(e.target.value);
        setFormData({
          ...formData,
          paymentGatewayId: gatewayId,
    });

    if (gatewayId > 0) {
      setSelectedGateway(paymentGateways?.find(gateway => gateway.paymentGatewayId === gatewayId));
      await dispatch(fetchPaymentGatewayKeysSchema({ paymentGatewayId: gatewayId, distributorId }));
    } else {
      setSelectedGateway(null);
      dispatch(clearSelectedGatewaySchema());
    }
  };

  const handlePaymentGatewayConfigChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      paymentGatewayConfig: {
        ...formData.paymentGatewayConfig,
        [name]: value,
      },
    });
  };

  const handleUpdatePaymentGatewayConfig = async () => {
    if (formData.paymentGatewayId > 0) {
      await dispatch(updatePaymentGatewayConfig({
        distributorId,
        paymentGatewayId: formData.paymentGatewayId,
        config: formData.paymentGatewayConfig,
      }));
    }
  };

  const handleClosePaymentGatewayModal = () => {
    setShowPaymentGatewayModal(false);
    dispatch(clearMessages());
  };

  const handleAddIp = () => {
    if (newIp.trim() !== "") {
      setFormData({
        ...formData,
        whiteListedIps: [...formData.whiteListedIps, newIp],
      });
      setNewIp("");
    }
  };

  const handleRemoveIp = (index: number) => {
    const newIps = formData.whiteListedIps.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      whiteListedIps: newIps,
    });
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setFormData({
        ...formData,
        logo: file
      });
      
      // Create preview URL
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleContinue = async (e: React.FormEvent) => {
    e.preventDefault();
    let hasError = false;
    const errors = {
      currency: "",
      country: "",
      paymentType: "",
      name: "",
      businessName: "",
      password: "",
      hostname: "",
      contactInformation: {
        email: "",
        phone: "",
        contactPerson: "",
        address: {
          street: "",
          city: "",
          state: "",
          country: "",
          zipCode: "",
        },
      },
    };

    if (!formData.businessName) {
      hasError = true;
      errors.businessName = "Please enter valid business name";
    }

    if (!formData.name) {
      hasError = true;
      errors.name = "Please enter valid name";
    }

    if (!formData.country) {
      hasError = true;
      errors.country = "Please select a country";
    }

    if (!formData.currency) {
      hasError = true;
      errors.currency = "Please select a currency";
    }

    // Validate hostname (optional field, but if provided must be valid)
    if (formData.hostname && formData.hostname.trim()) {
      // Hostname should be a single word (no spaces), lowercase alphanumeric with hyphens/underscores
      const hostnameRegex = /^[a-z0-9]([a-z0-9_-]*[a-z0-9])?$/;
      if (!hostnameRegex.test(formData.hostname)) {
        hasError = true;
        errors.hostname = "Hostname must be lowercase, no spaces, and can only contain letters, numbers, hyphens, and underscores";
      } else if (formData.hostname.length > 63) {
        hasError = true;
        errors.hostname = "Hostname must be 63 characters or less";
      }
    }

    if (hasError) {
      setFormErrors(errors);
      
      // Get the first error field and scroll to it
      const errorFields = [];
      if (errors.businessName) errorFields.push({ key: 'businessName', id: 'business_name' });
      if (errors.name) errorFields.push({ key: 'name', id: 'name' });
      if (errors.country) errorFields.push({ key: 'country', id: 'country' });
      if (errors.currency) errorFields.push({ key: 'currency', id: 'currency' });
      if (errors.hostname) errorFields.push({ key: 'hostname', id: 'wlHostname' });

      if (errorFields.length > 0) {
        const firstError = errorFields[0];
        const errorElement = document.getElementById(firstError.id);
        if (errorElement) {
          errorElement.scrollIntoView({ behavior: "smooth", block: "center" });
          errorElement.focus();
        }
      }
      
      return;
    }

    setShowBuildCatalogue(true);
  };

  const handleBuildCatalogueBack = () => {
    setShowBuildCatalogue(false);
  };

  if (showBuildCatalogue) {
    return (
      <EditBuildCataloguePage
        {...formData}
        logo={formData.logo}
        distributorId={distributorId}
        onBack={handleBuildCatalogueBack}
        onBackListing={onBack}
      />
    );
  }

  return (
    <div className="bg-white h-full">
      {/* Header */}
      <div className="flex border-b border-cardborder mb-8 flex-col md:flex-row md:items-center md:justify-between max-w-7xl mx-auto px-7 py-8">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="p-1.5 hover:bg-gray-100 rounded-full transition-colors"
            aria-label="Go back"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <h1 className="text-2xl font-bold text-gray-900">
            Edit Distributor
          </h1>
          </div>
        </div>

      {/* Content Section */}
      <form onSubmit={handleContinue} className="max-w-7xl mx-auto px-8 sm:px-6 lg:px-8 space-y-8">
        {/* Error State */}
        {error && (
          <div className="bg-white rounded-xl p-6 flex flex-col justify-center min-h-[120px] transition-shadow duration-200 transition-colors border-[1px] border-cardborder">
            <div className="flex items-center gap-2 justify-center text-red-500">
              <AlertCircle className="w-5 h-5" />
              <span>{error}</span>
            </div>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="bg-white rounded-xl p-6 flex flex-col justify-center min-h-[120px] transition-shadow duration-200 transition-colors border-[1px] border-cardborder">
            <div className="flex justify-center items-center gap-2">
              <Loader2 className="w-10 h-10 animate-spin text-brand/80" />
              <span className="text-sm text-gray-500">Loading...</span>
          </div>
          </div>
        )}

        {/* Form Content */}
        {!loading && !error && (
          <>
            {/* Basic Information Card */}
            <div className="bg-white rounded-xl p-6 flex flex-col justify-start min-h-[120px] transition-shadow duration-200 transition-colors border-[1px] border-cardborder">
              <h2 className="text-lg font-semibold mb-4">
                Basic Information
              </h2>
              <div className="space-y-6">
                {/* Distributor Account Name */}
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    Distributor Account Name
                  </label>
                  <input
                    id="name"
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Enter Short Name"
                    className={`block w-full px-3 py-2 border ${formErrors.name ? 'border-red-500' : 'border-gray-300'} rounded-md bg-white text-sm text-gray-700 focus:outline-none`}
                  />
                  {formErrors.name && (
                    <div className="text-red-500 text-sm mt-1">
                      {formErrors.name}
                    </div>
                  )}
                </div>

                {/* Distributor Legal Name */}
                <div>
                  <label htmlFor="business_name" className="block text-sm font-medium text-gray-700 mb-2">
                    Distributor Legal Name
                  </label>
                  <input
                    id="business_name"
                    type="text"
                    name="businessName"
                    value={formData.businessName}
                    onChange={handleChange}
                    placeholder="Distributor Legal Business Name"
                    className={`block w-full px-3 py-2 border ${formErrors.businessName ? 'border-red-500' : 'border-gray-300'} rounded-md bg-white text-sm text-gray-700 focus:outline-none`}
                  />
                  {formErrors.businessName && (
                    <div className="text-red-500 text-sm mt-1">
                      {formErrors.businessName}
                    </div>
                  )}
                </div>

                {/* Hostname */}
                <div>
                  <label htmlFor="wlHostname" className="block text-sm font-medium text-gray-700 mb-2">
                    Hostname
                  </label>
                  <input
                    id="wlHostname"
                    type="text"
                    value={formData.hostname}
                    onChange={(e) => {
                      // Convert to lowercase and remove spaces
                      const value = e.target.value.toLowerCase().replace(/\s+/g, '');
                      setFormData({
                        ...formData,
                        hostname: value
                      });
                    }}
                    placeholder="Enter hostname"
                    className={`block w-full px-3 py-2 border ${formErrors.hostname ? 'border-red-500' : 'border-gray-300'} rounded-md bg-white text-sm text-gray-700 focus:outline-none`}
                  />
                  {formErrors.hostname && (
                    <div className="text-red-500 text-sm mt-1">
                      {formErrors.hostname}
                    </div>
                  )}
                  <p className="text-sm text-gray-500 mt-1">
                    This will be used to identify the distributor in the buy portal URL (e.g., {formData.hostname || "hostname"}.wl.fincentive.co). Must be lowercase, no spaces.
                  </p>
                </div>

                {/* Distributor Logo */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Distributor Logo
                  </label>
                  <div className="flex items-center gap-4">
                    {logoPreview && (
                      <img 
                        src={logoPreview}
                        alt="Logo preview" 
                        className="w-24 h-24 object-contain border border-gray-300 rounded-lg p-2"
                      />
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleLogoChange}
                      className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-brand file:text-white hover:file:bg-brand/80"
                    />
                  </div>
                  <p className="text-sm text-gray-500 mt-1">
                    Recommended size: 200x200px. Max file size: 2MB
                  </p>
                </div>

                {/* Country and Currency Row */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Country */}
                  <div>
                    <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-2">
                      Country
                    </label>
                    <div className="relative">
                      <select
                        id="country"
                        name="country"
                        value={formData.country}
                        onChange={handleChange}
                        className={`block w-full px-3 py-2 border ${formErrors.country ? 'border-red-500' : 'border-gray-300'} rounded-md bg-white text-sm text-gray-700 focus:outline-none appearance-none`}
                      >
                    <option value="" disabled>Select Country</option>
                    <option value="IND">India</option>
                      </select>
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-400">
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    </div>
                    {formErrors.country && (
                      <div className="text-red-500 text-sm mt-1">
                        {formErrors.country}
                      </div>
                    )}
                  </div>

                  {/* Currency */}
                  <div>
                    <label htmlFor="currency" className="block text-sm font-medium text-gray-700 mb-2">
                      Distributor Wallet Currency
                    </label>
                    <div className="relative">
                      <select
                        id="currency"
                        name="currency"
                        value={formData.currency}
                        onChange={handleChange}
                        className={`block w-full px-3 py-2 border ${formErrors.currency ? 'border-red-500' : 'border-gray-300'} rounded-md bg-white text-sm text-gray-700 focus:outline-none appearance-none`}
                      >
                    <option value="" disabled>Select Currency</option>
                    <option value={code}>{code}</option>
                    {formData.currency && formData.currency !== code && (
                      <option value={formData.currency}>{formData.currency}</option>
                    )}
                      </select>
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-400">
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    </div>
                    {formErrors.currency && (
                      <div className="text-red-500 text-sm mt-1">
                        {formErrors.currency}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Information Card */}
            <div className="bg-white rounded-xl p-6 flex flex-col justify-start min-h-[120px] transition-shadow duration-200 transition-colors border-[1px] border-cardborder">
              <h2 className="text-lg font-semibold mb-4">
                Contact Information
              </h2>
              <div className="space-y-6">
                {/* Contact Email and Phone Row */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="contactEmail" className="block text-sm font-medium text-gray-700 mb-2">
                      Contact Email
                    </label>
                    <input
                      id="contactEmail"
                      type="email"
                      name="contactInformation.email"
                      value={formData.contactInformation.email}
                      onChange={handleChange}
                      placeholder="Enter Contact Email"
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-sm text-gray-700 focus:outline-none"
                    />
                  </div>

                  <div>
                    <label htmlFor="contactPhone" className="block text-sm font-medium text-gray-700 mb-2">
                      Contact Phone
                    </label>
                    <input
                      id="contactPhone"
                      type="text"
                      name="contactInformation.phone"
                      value={formData.contactInformation.phone}
                      onChange={handleChange}
                      placeholder="Enter Contact Phone"
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-sm text-gray-700 focus:outline-none"
                    />
                  </div>
                </div>

                {/* Contact Person */}
                <div>
                  <label htmlFor="contactPerson" className="block text-sm font-medium text-gray-700 mb-2">
                    Contact Person
                  </label>
                  <input
                    id="contactPerson"
                    type="text"
                    name="contactInformation.contactPerson"
                    value={formData.contactInformation.contactPerson}
                    onChange={handleChange}
                    placeholder="Enter Contact Person"
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-sm text-gray-700 focus:outline-none"
                  />
                </div>

                {/* Address Section */}
                <div className="space-y-4">
                  <h3 className="text-md font-medium text-gray-900">Address</h3>
                  
                  {/* Street */}
                  <div>
                    <label htmlFor="addressStreet" className="block text-sm font-medium text-gray-700 mb-2">
                      Street
                    </label>
                    <input
                      id="addressStreet"
                      type="text"
                      name="contactInformation.address.street"
                      value={formData.contactInformation.address.street}
                      onChange={handleChange}
                      placeholder="Enter Street"
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-sm text-gray-700 focus:outline-none"
                    />
                  </div>

                  {/* City, State, Country Row */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                      <label htmlFor="addressCity" className="block text-sm font-medium text-gray-700 mb-2">
                        City
                      </label>
                      <input
                        id="addressCity"
                        type="text"
                        name="contactInformation.address.city"
                        value={formData.contactInformation.address.city}
                        onChange={handleChange}
                        placeholder="Enter City"
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-sm text-gray-700 focus:outline-none"
                      />
                    </div>

                    <div>
                      <label htmlFor="addressState" className="block text-sm font-medium text-gray-700 mb-2">
                        State
                      </label>
                      <input
                        id="addressState"
                        type="text"
                        name="contactInformation.address.state"
                        value={formData.contactInformation.address.state}
                        onChange={handleChange}
                        placeholder="Enter State"
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-sm text-gray-700 focus:outline-none"
                      />
                    </div>

                    <div>
                      <label htmlFor="addressCountry" className="block text-sm font-medium text-gray-700 mb-2">
                        Country
                      </label>
                      <input
                        id="addressCountry"
                        type="text"
                        name="contactInformation.address.country"
                        value={formData.contactInformation.address.country}
                        onChange={handleChange}
                        placeholder="Enter Country"
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-sm text-gray-700 focus:outline-none"
                      />
                    </div>
                  </div>

                  {/* Zip Code */}
                  <div>
                    <label htmlFor="addressZipCode" className="block text-sm font-medium text-gray-700 mb-2">
                      Zip Code
                    </label>
                    <input
                      id="addressZipCode"
                      type="text"
                      name="contactInformation.address.zipCode"
                      value={formData.contactInformation.address.zipCode}
                      onChange={handleChange}
                      placeholder="Enter Zip Code"
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-sm text-gray-700 focus:outline-none"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Settings Card */}
            <div className="bg-white rounded-xl p-6 flex flex-col justify-start min-h-[120px] transition-shadow duration-200 transition-colors border-[1px] border-cardborder">
              <h2 className="text-lg font-semibold mb-4">
                Settings
              </h2>
              <div className="space-y-6">
                {/* Whitelisted IPs */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Whitelisted IPs
                  </label>
                  <div className="border border-gray-300 rounded-lg p-4 bg-white">
                    <div className="flex items-center gap-3 mb-3">
                      <input
                        type="text"
                        value={newIp}
                        onChange={(e) => setNewIp(e.target.value)}
                        placeholder="Enter IP address"
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md bg-white text-sm text-gray-700 focus:outline-none"
                      />
                      <button
                        type="button"
                        onClick={handleAddIp}
                        className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500 flex items-center gap-2"
                      >
                        <Plus className="w-4 h-4" />
                        Add IP
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {formData.whiteListedIps.map((ip, index) => (
                        <span key={index} className="bg-gray-100 rounded-full px-3 py-1 text-sm flex items-center gap-2">
                          {ip}
                          <button
                            type="button"
                            onClick={() => handleRemoveIp(index)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Checkbox */}
                <div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                    type="checkbox"
                    name="isNonUniqueReferenceIdAllowed"
                    checked={formData.isNonUniqueReferenceIdAllowed}
                    onChange={handleChange}
                      className="w-4 h-4 text-brand border-gray-300 rounded focus:ring-brand focus:ring-2"
                    />
                    <span className="text-sm font-medium text-gray-700">
                      Allow Non-Unique Reference IDs
                    </span>
                  </label>
                </div>

                {/* Require Purchase Orders */}
                <div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      name="isPurchaseOrderRequired"
                      checked={formData.isPurchaseOrderRequired}
                      onChange={handleChange}
                      className="w-4 h-4 text-brand border-gray-300 rounded focus:ring-brand focus:ring-2"
                    />
                    <span className="text-sm font-medium text-gray-700">
                      Require Purchase Orders for this Distributor
                    </span>
                  </label>
                </div>

                {/* Allow Purchase Order Creation */}
                <div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      name="allowPurchaseOrderCreation"
                      checked={formData.allowPurchaseOrderCreation}
                      onChange={handleChange}
                      className="w-4 h-4 text-brand border-gray-300 rounded focus:ring-brand focus:ring-2"
                    />
                    <span className="text-sm font-medium text-gray-700">
                      Allow Purchase Order Creation for this Distributor
                    </span>
                  </label>
                </div>

                {/* Payment Terms */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Distributor Wallet Payment Terms
                  </label>
                  <div className="flex gap-6">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="paymentType"
                        value="0"
                        checked={formData.paymentType === 0}
                        onChange={handleChange}
                        className="w-4 h-4 text-brand border-gray-300 focus:ring-brand focus:ring-2"
                      />
                      <span className="text-sm font-medium text-gray-700">Prepaid</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="paymentType"
                        value="1"
                        checked={formData.paymentType === 1}
                        onChange={handleChange}
                        className="w-4 h-4 text-brand border-gray-300 focus:ring-brand focus:ring-2"
                      />
                      <span className="text-sm font-medium text-gray-700">Postpaid</span>
                    </label>
                  </div>
                  {formErrors.paymentType && (
                    <div className="text-red-500 text-sm mt-1">
                    {formErrors.paymentType}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* WL-Portal Details Card */}
            <div className="bg-white rounded-xl p-6 flex flex-col justify-start min-h-[120px] transition-shadow duration-200 transition-colors border-[1px] border-cardborder">
              <h2 className="text-lg font-semibold mb-4">
                WL-Portal Details
              </h2>
              <div className="space-y-6">
                {/* Payment Gateway */}
                <div>
                  <label htmlFor="wlPaymentGateway" className="block text-sm font-medium text-gray-700 mb-2">
                    Payment Gateway
                  </label>
                  <div className="relative">
                    <select
                      id="wlPaymentGateway"
                  value={formData.paymentGatewayId ? formData.paymentGatewayId.toString() : ""}
                  onChange={handlePaymentGatewayChange}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-sm text-gray-700 focus:outline-none appearance-none"
                >
                  <option value="">No payment gateway</option>
                  {paymentGateways?.map((gateway) => (
                    <option key={gateway.paymentGatewayId} value={gateway.paymentGatewayId}>
                      {gateway.name}
                    </option>
                  ))}
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-400">
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>
                </div>

                {/* Color */}
                <div>
                  <label htmlFor="wlColor" className="block text-sm font-medium text-gray-700 mb-2">
                    Color
                  </label>
                  <div className="flex items-center gap-3">
                  <input 
                    type="color"
                    value={formData.wlPortalDetails.color}
                    onChange={(e) => setFormData({
                      ...formData,
                      wlPortalDetails: {
                        ...formData.wlPortalDetails,
                        color: e.target.value
                      }
                    })}
                      className="w-15 h-10 p-1 border border-gray-300 rounded-lg cursor-pointer"
                    />
                    <input 
                    type="text" 
                    value={formData.wlPortalDetails.color}
                    onChange={(e) => setFormData({
                      ...formData,
                      wlPortalDetails: {
                        ...formData.wlPortalDetails,
                        color: e.target.value
                      }
                    })}
                    placeholder="#000000"
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-sm text-gray-700 focus:outline-none"
                  />
                </div>
                </div>
          </div>
      </div>

            {/* Submit Button */}
            <div className="flex justify-between pb-8">
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={onBack}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none"
                >
                  Cancel
                </button>
                {onOpenAlertSettings && (
                  <button
                    type="button"
                    onClick={onOpenAlertSettings}
                    className="px-4 py-2 text-sm font-medium text-brand border border-brand rounded-md hover:bg-brand/5 focus:outline-none"
                  >
                    Wallet alert settings
                  </button>
                )}
              </div>

              <button
                type="submit"
                className="px-4 py-2 text-sm font-medium text-white rounded-md bg-brand hover:bg-brand/80 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={loading}
          >
            {loading ? (
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Processing...
                  </div>
                ) : (
                  "Continue"
                )}
              </button>
            </div>
          </>
        )}
      </form>
    </div>
  );
};

export default EditDistributorPage;